import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { binderyApi } from "@/lib/api/bindery";

interface FormState {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  defaultTurnaroundDays: string;
  notes: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  name: "",
  contactPerson: "",
  email: "",
  phone: "",
  address: "",
  defaultTurnaroundDays: "21",
  notes: "",
  isActive: true,
};

export default function BinderyVendorFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    binderyApi.vendors
      .getById(id)
      .then((v) => {
        setForm({
          name: v.name,
          contactPerson: v.contactPerson ?? "",
          email: v.email ?? "",
          phone: v.phone ?? "",
          address: v.address ?? "",
          defaultTurnaroundDays: String(v.defaultTurnaroundDays),
          notes: v.notes ?? "",
          isActive: v.isActive,
        });
      })
      .catch(() => toast.error("Failed to load vendor"))
      .finally(() => setLoading(false));
  }, [id, toast]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contactPerson: form.contactPerson.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        defaultTurnaroundDays: Number(form.defaultTurnaroundDays) || 21,
        notes: form.notes.trim() || undefined,
        isActive: form.isActive,
      };
      const saved = isEdit
        ? await binderyApi.vendors.update(id!, payload)
        : await binderyApi.vendors.create(payload);
      toast.success(`Vendor ${isEdit ? "updated" : "created"}`);
      navigate(`/bindery/vendors/${saved.id}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, ""));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <form className="space-y-6" onSubmit={submit}>
        <PageHeader
          title={isEdit ? "Edit bindery vendor" : "New bindery vendor"}
          description="External bindery or book-repair service provider."
          breadcrumbItems={[
            { label: "Bindery vendors", href: "/bindery/vendors" },
            { label: isEdit ? "Edit vendor" : "Add vendor" },
          ]}
          showSearch={false}
          titleSize="md"
          actions={
            <div className="flex gap-2">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          }
        />

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="name" required>
                  Name
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Vendor or bindery name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="contact">Contact person</Label>
                <Input
                  id="contact"
                  value={form.contactPerson}
                  onChange={(e) => update("contactPerson", e.target.value)}
                  placeholder="Primary contact name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="contact@vendor.example"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+234 …"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  placeholder="Street, city"
                />
              </div>
              <div>
                <Label htmlFor="days">Default turnaround (days)</Label>
                <Input
                  id="days"
                  type="number"
                  min={1}
                  max={365}
                  value={form.defaultTurnaroundDays}
                  onChange={(e) => update("defaultTurnaroundDays", e.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => update("isActive", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="active" className="mb-0">
                  Active
                </Label>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Internal notes about this vendor"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </PageContainer>
  );
}
